import React from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../context/ThemeContext';

/**
 * The product footer — attribution and copyright, shown at the end of every
 * in-shell page and under the auth card.
 *
 * Ported from the Ecare system (`rmc2-er-system`) so the two RMC2 products
 * carry the same sign-off. Two deliberate differences: the Ecare original
 * hardcodes `bg-white` + `text-slate-*`, which pins it to light mode and is a
 * Rule-56 block here, so this renders on theme tokens instead; and the
 * HunterLabs mark follows the theme the same way `<BrandLogo>` does (the black
 * glyph on light, the white one on dark) rather than being a single asset.
 *
 * The copyright year is fixed rather than `new Date().getFullYear()` — a
 * notice is a statement about when the work was published, not a clock, and a
 * year that silently advances on New Year's Eve is a claim nobody reviewed.
 */
export default function Footer() {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const hunterlabsLogo = isDark
    ? '/logo192-hlabs-white.png'
    : '/logo192-hlabs-black.png';

  return (
    <footer className="rmc2-footer">
      <div className="rmc2-footer-inner">
        <p className="rmc2-footer-line">
          {t('footer.poweredBy')}
          <a
            className="rmc2-footer-link rmc2-footer-vendor"
            href="https://hunterlabs.solutions/"
            target="_blank"
            rel="noreferrer"
          >
            <img
              className="rmc2-footer-mark"
              src={hunterlabsLogo}
              alt={t('footer.vendorLogoAlt')}
              width={20}
              height={20}
              loading="lazy"
            />
            {t('footer.vendor')}
          </a>
        </p>

        <p className="rmc2-footer-line rmc2-footer-copy">
          {t('footer.copyright', { year: 2026 })}{' '}
          <a
            className="rmc2-footer-link"
            href="https://www.rmc2consultancycorp.com/"
            target="_blank"
            rel="noreferrer"
          >
            {t('footer.operator')}
          </a>
          {t('footer.rightsReserved')}
        </p>
      </div>
    </footer>
  );
}
